import { isFederated } from '@frontend-platform/federation-runtime';
import { getSharedState } from '@frontend-platform/host-state';
import { useSharedKey } from './use-shared-key';

const BOT_KEY = 'currentBot';
const DYNAMIC_ENV = 'DYNAMIC_ENV';

function readEnvProductTypeFromHostContext(): boolean | null {
  const bot = getSharedState()?.bots?.currentBot;
  if (!bot) return null;
  return (bot.envProductType as string | undefined) === DYNAMIC_ENV;
}

// Standalone-dev only. aistudio's own SET_CURRENT_BOT mutation ALSO writes
// the live bot to this exact unprefixed localStorage key
// (client/views/Bots/store/mutations.js) — and since Module Federation loads
// this app into the same window/origin as the host (no iframe), reading this
// key while federated could transparently pick up aistudio's own real bot
// data instead of the standalone-dev seed. That's not a fallback we want: it
// can read a stale bot (e.g. left over from a previous session) instead of
// just waiting one tick for @frontend-platform/host-state to publish the current one.
function readFromLocalStorage(): boolean {
  try {
    const bot = JSON.parse(localStorage.getItem(BOT_KEY) || '{}');
    return bot?.envProductType === DYNAMIC_ENV;
  } catch {
    return false;
  }
}

function getIsDynamicEnvSnapshot(): boolean {
  if (isFederated()) return readEnvProductTypeFromHostContext() ?? false;
  return readFromLocalStorage();
}

/**
 * Returns true when the current bot is a `DYNAMIC_ENV` (env-promotion / "EP")
 * bot. Mirrors aistudio's `useBotProperties().isEnvProductTypeDynamic`, which
 * reads `store.state.bots.currentBot.envProductType`.
 *
 * Reactive rather than a one-time read: under federation, @frontend-platform/host-state can
 * still be a tick behind `mount()` starting (aistudio's own bot/environment
 * bootstrap is async), so a `useState` initializer risked locking in `false`
 * forever if it ran before the host's first publish. Subscribing via
 * `useSyncExternalStore` means a late-arriving publish self-corrects on the
 * very next render instead of leaving this stuck wrong for the whole mount.
 */
export function useIsDynamicEnv(): boolean {
  return useSharedKey('bots', getIsDynamicEnvSnapshot);
}
